import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Divider, Typography } from '@material-ui/core';
import { useTranslations } from 'utils/useTranslations';
import { CaseListItem } from 'pages/User/PatientCases/types';
import { format, isValid } from 'date-fns';
import clsx from 'clsx';
import useCaseFetch from 'api/Case';
import usePermissions from 'utils/usePermissions';
import { roundTo2 } from 'utils/roundTo2';
import { useStyles } from './styles';

interface ParamType {
  patient: string;
  caseId: string;
}

const PatientAccountItem = ({ caseItem, withButton = false }: { caseItem: CaseListItem; withButton?: boolean }) => {
  const intl = useTranslations();
  const styles = useStyles();
  const [loading, setLoading] = useState(false);
  const { patient, caseId } = useParams<ParamType>();
  const { checkOut } = useCaseFetch();
  const { canCheckoutAddAndViewPaymentsInBills } = usePermissions();
  const isAllResolved = () => {
    let isResolved = true;
    if (caseItem && caseItem.medicalRecords) {
      caseItem.medicalRecords.map((medRecord: any) => {
        if (medRecord && medRecord.catalog) {
          medRecord.catalog.map((catalogItem: any) => {
            if (!catalogItem.resolved) {
              isResolved = false;
            }
            return null;
          });
        }
        return null;
      });
    }

    return isResolved;
  };

  const handleCheckout = async () => {
    try {
      setLoading(true);
      await checkOut({ caseId });
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };
  const checkOutAvailable = (caseItem.checkOut || 0) < caseItem.updatedAt && caseItem.total <= caseItem.paidTotal && isAllResolved();

  return (
    <Box className={styles.billsContainer}>
      <Box className={styles.billsSubContainer}>
        <Typography className={styles.billsHeader}>
          {intl('patient.case')} {isValid(new Date(caseItem.name)) ? format(new Date(caseItem.name), 'dd.MM.yyyy') : caseItem.name}
          {caseItem?.responseFromService && (
            <Typography className={styles.responseFromService} component="span">
              ({caseItem?.responseFromService?.mensaje})
            </Typography>
          )}
          {loading && <CircularProgress size={16} />}
        </Typography>
        {canCheckoutAddAndViewPaymentsInBills && (
          <Box className={styles.boxForButtons}>
            <Box className={styles.boxForButton}>
              {withButton && (
                <Link
                  to={`/user/patient_records/${patient}/accounts/${caseItem._id}/payments`}
                  className={clsx(styles.resetLinkDecorationRecords, styles.buttonPositioning)}
                >
                  <Button className={styles.caseBtnText} color="primary">
                    {intl('patient.bills')}
                  </Button>
                </Link>
              )}
            </Box>
            <Box className={styles.boxForButton}>
              {withButton && (
                <Button
                  onClick={handleCheckout}
                  disabled={!checkOutAvailable || loading}
                  className={styles.caseBtnText}
                  variant="contained"
                  color="primary"
                >
                  {intl('patient.checkout')}
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <Divider />
      <Box className={styles.billsBoxItem}>
        <Box className={styles.caseInnerItem}>
          <Typography className={styles.contentText}>{intl('case.admission_date')}</Typography>
          <Typography className={styles.contentText}>{format(new Date(caseItem.createdAt), 'dd.MM.yyyy')}</Typography>
        </Box>
        <Box className={styles.caseInnerItem}>
          <Typography className={styles.contentText}>{intl('case.discharge_date')}</Typography>
          <Typography className={styles.contentText}>{(caseItem.checkOut && format(new Date(caseItem.checkOut), 'dd.MM.yyyy')) || '-'}</Typography>
        </Box>
        <Box className={styles.caseInnerItem}>
          <Typography className={styles.contentText}>{intl('case.estimate')}</Typography>
          <Typography className={styles.contentText}>{caseItem.budget ? intl(`case.budgetOption.${caseItem.budget}`) : '-'}</Typography>
        </Box>
        {canCheckoutAddAndViewPaymentsInBills && (
          <>
            <Box className={styles.caseInnerItem}>
              <Typography className={styles.contentText}>{intl('case.totalAccount')}</Typography>
              <Typography className={styles.contentText}>{roundTo2(caseItem.total)}</Typography>
            </Box>
            <Box className={styles.caseInnerItem}>
              <Typography className={styles.contentText}>{intl('case.totalAccountPaid')}</Typography>
              <Typography className={styles.contentText}>{roundTo2(caseItem.paidTotal)}</Typography>
            </Box>
            <Box className={styles.caseInnerItem}>
              <Typography className={styles.contentText}>{intl('case.amountToPay')}</Typography>
              <Typography className={styles.contentText}>{roundTo2(caseItem.total - caseItem.paidTotal)}</Typography>
            </Box>
          </>
        )}
        {!withButton && (
          <Link to={`/user/patient_records/${patient}/accounts/${caseItem._id}`} className={styles.resetLinkDecorationRecords}>
            <Button className={styles.caseBtnText} variant="contained" color="primary">
              {intl('view.case')}
            </Button>
          </Link>
        )}
      </Box>
    </Box>
  );
};

export default PatientAccountItem;
